import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { useAuthStore } from 'lib/stores/authStore';

/**
 * Exchange-level subaccount selector (0–63). 0 is the default/primary subaccount
 * and omitting the field is equivalent to sending 0, so the frontend omits the
 * field when 0 is selected to keep requests unchanged for primary-only flows.
 *
 * The selection is persisted per-device and is only reachable via the "Sub
 * accounts" panel on the `/account` page, which itself is gated on whether the
 * user has multiple subaccounts server-side (detected via `GET /users/{id}/balances`).
 */
export const MIN_SUBACCOUNT_NUMBER = 0;
export const MAX_SUBACCOUNT_NUMBER = 63;

/** Lowest ISV partner subaccount; mirrors the backend's ISVSubaccountStart. */
export const ISV_SUBACCOUNT_START = 4096;

export function isIsvSubaccount(subaccountNumber: number): boolean {
    return subaccountNumber >= ISV_SUBACCOUNT_START;
}

/** Only user-managed subaccounts (0–63) can be switched to or transferred between; the backend rejects everything else. */
export function isUserManagedSubaccount(subaccountNumber: number): boolean {
    return (
        Number.isInteger(subaccountNumber) &&
        subaccountNumber >= MIN_SUBACCOUNT_NUMBER &&
        subaccountNumber <= MAX_SUBACCOUNT_NUMBER
    );
}

function isValidSubaccountNumber(value: number): boolean {
    return isUserManagedSubaccount(value);
}

interface SubAccountState {
    /** Currently active subaccount number. Defaults to 0 (primary). */
    subaccountNumber: number;
    /**
     * True when the backend reports a non-primary subaccount on
     * `GET /users/{id}/balances`. Drives visibility of the account UI, the
     * portfolio-label indicator, slow-polling, and WS subaccount filtering.
     */
    subaccountEnabled: boolean;
    actions: {
        subaccountSelected: (subaccount: number) => void;
        subaccountEnabledUpdated: (enabled: boolean) => void;
        reset: () => void;
    };
}

export const useSubAccountStore = create<SubAccountState>()(
    persist(
        (set) => ({
            subaccountNumber: MIN_SUBACCOUNT_NUMBER,
            subaccountEnabled: false,
            actions: {
                subaccountSelected: (subaccount) => {
                    if (!isValidSubaccountNumber(subaccount)) return;
                    set({ subaccountNumber: subaccount });
                },
                subaccountEnabledUpdated: (enabled) => {
                    set((state) => {
                        // Capability lost (e.g. logout, or a different user logged in
                        // on the same browser whose `/balances` returns a single row):
                        // drop any persisted non-zero selection so the axios
                        // interceptor and WS subscribe payloads stop scoping
                        // requests to a subaccount the new user doesn't have.
                        if (!enabled && state.subaccountNumber !== MIN_SUBACCOUNT_NUMBER) {
                            return {
                                subaccountEnabled: false,
                                subaccountNumber: MIN_SUBACCOUNT_NUMBER,
                            };
                        }
                        if (state.subaccountEnabled === enabled) return state;
                        return { subaccountEnabled: enabled };
                    });
                },
                reset: () => set({ subaccountNumber: MIN_SUBACCOUNT_NUMBER, subaccountEnabled: false }),
            },
        }),
        {
            name: 'kalshi-subaccount',
            // Only persist the selection; `subaccountEnabled` is derived from
            // a query and should be re-hydrated from the server each session.
            partialize: (state) => ({ subaccountNumber: state.subaccountNumber }),
        },
    ),
);

/**
 * Non-hook accessor safe to use from axios interceptors and WS subscribe
 * payloads that run outside of React render. Always returns a validated
 * integer in the 0–63 range.
 */
export function getSubaccountNumber(): number {
    const value = useSubAccountStore.getState().subaccountNumber;
    return isValidSubaccountNumber(value) ? value : MIN_SUBACCOUNT_NUMBER;
}

/**
 * Drop the persisted subaccount selection (and `subaccountEnabled`) the moment
 * the active user changes — including logout. Without this, a non-zero
 * selection persisted by the previous user would scope axios interceptor /
 * WS payloads onto the new user's `/users/{id}/...` requests until the next
 * `/balances` poll resolves and `subaccountEnabledUpdated(false)` clears it.
 *
 * Module-side subscription mirrors the pattern in `userPositionsStore`. It's
 * idempotent (subscribing to zustand returns the same callback regardless of
 * call count) and browser-only since the auth store is only meaningful in
 * the browser. The `typeof === 'function'` guard keeps unit tests that mock
 * `useAuthStore` without a `subscribe` method from crashing at import time.
 */
if (typeof window !== 'undefined' && typeof useAuthStore.subscribe === 'function') {
    useAuthStore.subscribe((state, prev) => {
        if (state.userId === prev.userId) return;
        useSubAccountStore.getState().actions.reset();
    });
}
